import { Popup, PopupOptions, PopupsManager } from '@funtech-int/web-toolkit';
import { ReactElement } from 'react';
import { Offer, PaymentPlace } from '@funtech-int/yango-shared';

import { PAYMENT_WIDGET_POPUP_KEY } from './paymentManager';

type ToastNotificationType =
    | 'BOOKMARK'
    | 'DONE'
    | 'FAIL'
    | 'NOTINTERESTED'
    | 'SUCCESS'
    | 'UNBOOKMARK'
    | 'UNDONOTINTERESTED';

export type ToastNotificationViewVariant = 'BOTTOM' | 'BOTTOMDARK' | 'TOP' | 'TOPDARK';

export type ToastNotification = {
    type: ToastNotificationType;
    message: string;
    viewVariant: ToastNotificationViewVariant;
    showTime?: number;
};
export function isToast(popup: Popup<any>): popup is Popup<ToastNotification> {
    return popup.kind === 'toast';
}

interface Modal {
    content: ReactElement;
    className?: string;
    closeButtonClassName?: string;

    onClose?: () => void;
    // add more on demand
}

interface ContentModal {
    content: ReactElement;

    onClose?: () => void;
    // add more on demand
}

export function isModal(popup: Popup<any>): popup is Popup<Modal> {
    return popup.kind === 'modal';
}

export function isContentModal(popup: Popup<any>): popup is Popup<ContentModal> {
    return popup.kind === 'contentModal';
}

interface PaymentWidget {
    start: () => void;
}

type CheckoutCallbacks = Parameters<PaymentPlace['checkout']>[1];

export function isPaymentWidget(popup: Popup<any>): popup is Popup<PaymentWidget> {
    return popup.kind === 'paymentWidget';
}

interface PopupsHandler<T> {
    show(payload: T, options?: PopupOptions): number;
    hide(id: number): void;
}

export function createPopupHandler<T>(
    popups: PopupsManager,
    kind: 'toast' | 'modal' | 'contentModal' | 'paymentWidget'
): PopupsHandler<T> {
    return {
        show(payload: T, options: PopupOptions = { key: kind }) {
            return popups.show(kind, payload, options);
        },
        hide: (id: number) => {
            popups.hide(id);
        },
    };
}

export function createPaymentPopupHandler(popups: PopupsManager) {
    return {
        open(
            paymentPlace: PaymentPlace | null | undefined,
            offer: Offer,
            callbacks: CheckoutCallbacks = {}
        ) {
            if (!paymentPlace) return;

            let popupId = 0;
            const close = () => popups.hide(popupId);

            popupId = popups.show(
                PAYMENT_WIDGET_POPUP_KEY,
                {
                    start: () =>
                        paymentPlace
                            .checkout(offer, {
                                ...callbacks,
                                onClose: () => {
                                    callbacks.onClose?.();
                                    close();
                                },
                                onNeedAuth: () => {
                                    callbacks.onNeedAuth?.();
                                    close();
                                },
                            })
                            .catch(close),
                },
                { key: PAYMENT_WIDGET_POPUP_KEY }
            );
        },
    };
}

export type ToastHandler = PopupsHandler<ToastNotification>;

export type ModalHandler = PopupsHandler<Modal>;

export type ContentModalHandler = PopupsHandler<ContentModal>;
